<script setup lang="ts">
import type {
  StudentCourseListItem,
  StudentEnrolmentStatus,
} from '@/models/current-student-advanced-standing-form/api'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  selectableCourses: StudentCourseListItem[]
  selectedCourseId: string
  courseSelectClass: LegacyClassBinding
  invalidCourseMessage: string
  enrolmentStatus: StudentEnrolmentStatus | ''
  enrolmentStatusClass: LegacyClassBinding
  invalidEnrolmentStatusMessage: string
  handbookUrl: string
  showSpgTool: boolean
  isInternationalCourse: boolean
  sponsoredStudent: string
  isSponsoredStudentLocked: boolean
  isSponsoredStudent: boolean
  internationalStudentClass: LegacyClassBinding
  coursePageMessage: string
  coursePageMessageIsError: boolean
}>()

const emit = defineEmits<{
  'update:selectedCourseId': [value: string]
  'courseChange': []
  'update:enrolmentStatus': [value: StudentEnrolmentStatus]
  'enrolmentStatusChange': []
  'update:sponsoredStudent': [value: string]
  'sponsoredStudentChange': []
}>()

function onCourseInputChange(event: Event): void {
  emit('update:selectedCourseId', (event.target as HTMLSelectElement).value)
  emit('courseChange')
}

function onEnrolmentStatusInputChange(event: Event): void {
  emit('update:enrolmentStatus', (event.target as HTMLInputElement).value as StudentEnrolmentStatus)
  emit('enrolmentStatusChange')
}

function onSponsoredStudentInputChange(event: Event): void {
  emit('update:sponsoredStudent', (event.target as HTMLSelectElement).value)
  emit('sponsoredStudentChange')
}
</script>

<template>
  <fieldset>
    <legend>Course Details</legend>
    <div class="form-field requiredinput" :class="courseSelectClass">
      <label for="CourseInput">What course would you like the credit to be applied to:</label>
      <select
        id="CourseInput"
        :value="selectedCourseId"
        :disabled="selectableCourses.length === 0"
        required
        class="form-select"
        @change="onCourseInputChange"
      >
        <option
          v-for="course in selectableCourses"
          :key="course.student_course_id"
          :value="String(course.student_course_id)"
        >
          {{ course.course_code }} - {{ course.course_name }} - {{ course.course_campus_name }} - {{ course.course_admission_status_name }}
        </option>
        <option v-if="selectableCourses.length === 0" disabled>
          You have no courses, please enroll into one.
        </option>
      </select>
    </div>
    <div class="alert alert-danger" id="InvalidCourseMessageBox">{{ invalidCourseMessage }}</div>
  </fieldset>

  <fieldset>
    <legend>Enrolment status</legend>
    <div class="form-field requiredinput" :class="enrolmentStatusClass">
      <p>Which of the following best describes your enrolment status?</p>
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          name="studentEnrolmentStatus"
          id="studentEnrolmentStatusCommencing"
          value="Commencing"
          :checked="enrolmentStatus === 'Commencing'"
          @change="onEnrolmentStatusInputChange"
        >
        <label class="form-check-label" for="studentEnrolmentStatusCommencing" style="font-weight: 400">
          Commencing: I am a new student starting my first study period in a new course
        </label>
      </div>
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          name="studentEnrolmentStatus"
          id="studentEnrolmentStatusContinuing"
          value="Continuing"
          :checked="enrolmentStatus === 'Continuing'"
          @change="onEnrolmentStatusInputChange"
        >
        <label class="form-check-label" for="studentEnrolmentStatusContinuing" style="font-weight: 400">
          Continuing: I am a current student, continuing my studies in the same course
        </label>
      </div>
      <div class="alert alert-danger" id="InvalidEnrolmentStatusMessageBox">{{ invalidEnrolmentStatusMessage }}</div>
    </div>
  </fieldset>

  <fieldset>
    <legend>Majors, Minors, &amp; Specialisations</legend>
    <p>
      If your course has Majors, Minors or Specialisations, it is important that you select the option you wish to study in
      <a target="_blank" href="https://www.latrobe.edu.au/students/admin/forms/studentonline" rel="noreferrer">StudentOnline</a>
      prior to applying for Advanced Standing. <br>
      <b>Failure to complete this may result in your application being rejected or receiving less credit that you are entitled to.</b>
    </p>

    <p>
      If you are not sure if your course has Majors, Minors or Specialisations please consult the
      <a class="HandbookLink" :href="handbookUrl" target="_blank" rel="noreferrer"> University Handbook</a>.

      <span class="spgTool" :class="{ hidden: !showSpgTool }">
        The <a target="_blank" href="https://www.latrobe.edu.au/students/your-course/study-plan-guides" rel="noreferrer">Study Plan Guides tool</a> may also be of assistance.
      </span>
    </p>
  </fieldset>

  <div class="internationalStudentOnly" :class="{ hidden: !isInternationalCourse }">
    <fieldset>
      <legend>International Student Details</legend>
      <div class="form-field requiredinput" :class="internationalStudentClass">
        <label for="InternationalStudentUnder18Input">Are you Under 18, a U.S. Financial Aid or Sponsored Student</label>
        <select
          id="InternationalStudentUnder18Input"
          :value="sponsoredStudent"
          :disabled="isSponsoredStudentLocked"
          class="form-select"
          data-admissions-name="sponsored_student"
          data-admissions-group="StudentInfo"
          @change="onSponsoredStudentInputChange"
        >
          <option value="">Please Select</option>
          <option value="0">No</option>
          <option value="1">Yes</option>
        </select>
      </div>
      <div class="InternationalStudentUnder18Only" :class="{ hidden: !isSponsoredStudent }">
        <p>
          You need to seek approval before the Advanced Standing can be approved and attach
          evidence of supporting documentation from your Sponsor,
          US Financial Administrator or your under 18 guarantor.
          You will be able to attach files to your application at the end of this form.
        </p>
      </div>
      <h3>Permanent Residency or Post-Study Work Rights</h3>
      <p>
        If you apply for Australian Permanent Residence (in the future)
        under the Department of Home Affairs (previously named Department of Immigration and Border Protection)
        General Skilled Migration program or for a Temporary Graduate Visa (Post-Study Work Rights) you will need to
        meet the Australian study requirements. It is your responsibility to ensure you check these requirements
        carefully to ensure that you enrol appropriately. We recommend you seek advice on this before accepting
        your Advanced Standing Offer. Please refer to Department of Home Affairs' website at <a href="https://homeaffairs.gov.au" target="_blank" rel="noreferrer">homeaffairs.gov.au</a>
      </p>
      <h3>International Student Visas Holders</h3>
      <p>
        As you hold a student visa you must complete your course of study
        within the time stated on your Confirmation of Enrolment (CoE), to meet visa requirements. If your course
        length is reduced as a result of Advanced Standing (credit), La Trobe International will issue a new CoE.
        This means that you will be expected to complete your course based on the new course length, regardless of
        your visa expiry date. You are required to contact the Department of Home Affairs when you receive the updated CoE.
        La Trobe International can provide general advice only on matters related to international students including
        procedures for Student visa extension. It is advised to read further details at <a href="https://www.homeaffairs.gov.au/Trav/Stud" target="_blank" rel="noreferrer">www.homeaffairs.gov.au/Trav/Stud</a>
      </p>
      <p>
        After the college has assessed this application, you must send an email to the college to confirm acceptance of the Advanced Standing (credit) granted, within 5 business days.
      </p>
    </fieldset>
  </div>

  <div
    class="alert"
    id="coursePageError"
    :class="{ 'alert-info': !coursePageMessageIsError, 'alert-danger': coursePageMessageIsError }"
  >
    {{ coursePageMessage }}
  </div>
</template>
